/**
 * ResultScreenGlobal.tsx - Global results comparison for the Risky Choice Framing experiment
 * Last updated: 2025-11-17 14:20 UTC
 * Updated by: JulianPolly & Copilot
 */

import React, { useEffect, useState } from "react";
import { Bar, BarChart, Tooltip, XAxis, YAxis, ResponsiveContainer, LabelList, Legend } from "recharts";
import { useSession } from "../../../SessionContext";
import { ScreenConfig } from "../Sequences";
import { RiskyChoiceFramingKey } from "./RiskyChoiceFramingSequence";
import { useLanguage } from "../../../../context/LanguageContext";
import { colors, commonStyles, chartColors } from "../../../../components/colors";

// Colors for chart bars
const safeColor = chartColors.primary[0]; // e.g., blue-green
const riskyColor = chartColors.primary[1]; // e.g., blue

const cardStyle: React.CSSProperties = {
    ...commonStyles.card,
    margin: 0,
    minWidth: 880,
    maxWidth: 880,
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    minHeight: 420,
    boxSizing: "border-box"
};

export const ResultScreenGlobalComponent = () => {
    const { getGroupData, userData } = useSession();
    const { t, language } = useLanguage();
    const [dataOwnGroup, setDataOwnGroup] = useState<any[]>([]);
    const [globalRCF, setGlobalRCF] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingGlobal, setLoadingGlobal] = useState(true);

    // Übersetzungen laden
    const loadingText = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'loading');
    const yourClassTitle = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'yourClass');
    const allClassesTitle = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'allClasses');
    const scenario1 = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'scenario1');
    const scenario2 = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'scenario2');
    const option1Label = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'option1');
    const option2Label = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'option2');
    const yAxisLabel = t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'yAxisLabel');

    // Custom Tooltip with colored value and percentage
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const total = payload.reduce((sum: number, p: any) => sum + (p.value || 0), 0);
            return (
                <div style={{
                    background: "#fff",
                    border: `1px solid ${colors.silver}`,
                    padding: 12,
                    borderRadius: 8,
                    fontSize: 14,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
                }}>
                    <div style={{
                        fontWeight: 600,
                        marginBottom: 6,
                        color: colors.brillantBlue
                    }}>
                        {label}
                    </div>
                    {payload.map((p: any) => {
                        const perc = total > 0 ? Math.round((p.value / total) * 100) : 0;
                        return (
                            <div key={p.dataKey} style={{ color: p.color || colors.textMedium }}>
                                {p.name}: {p.value} ({perc}%)
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    // Eigene Gruppe bestimmen (with robust normalization)
    const eigeneGruppe = userData?.gruppe ?? "";
    const normalizeGroup = (name: string | undefined | null) =>
        (name ?? "").trim().toLowerCase();

    useEffect(() => {
        setLoading(true);
        getGroupData().then(allData => {
            // For both dilemmas ("1" and "2"), collect counts for Option 1 and Option 2 for own group
            const ownGroupCounts = [
                { scenario: scenario1, [option1Label]: 0, [option2Label]: 0 },
                { scenario: scenario2, [option1Label]: 0, [option2Label]: 0 }
            ];

            const userGroupNorm = normalizeGroup(eigeneGruppe);

            allData?.forEach((dat: any) => {
                const gruppe = dat.gruppe;
                const gruppeNorm = normalizeGroup(gruppe);
                if (gruppeNorm !== userGroupNorm) return;

                if (dat[RiskyChoiceFramingKey]) {
                    ["1", "2"].forEach((index, i) => {
                        const selections = dat[RiskyChoiceFramingKey]?.[index]?.selections;
                        if (Array.isArray(selections) && selections.length > 0) {
                            const lastSelection = selections[selections.length - 1];
                            if (lastSelection?.choice === 1) ownGroupCounts[i][option1Label]++;
                            if (lastSelection?.choice === 2) ownGroupCounts[i][option2Label]++;
                        }
                    });
                }
            });

            setDataOwnGroup(ownGroupCounts);
            setLoading(false);
        }).catch(error => {
            setLoading(false);
            console.error("Error fetching group data:", error);
        });
    }, [getGroupData, eigeneGruppe, scenario1, scenario2, option1Label, option2Label]);

    useEffect(() => {
        setLoadingGlobal(true);
        fetch("/api/aggregateGlobalData")
            .then(res => res.json())
            .then((apiJson) => {
                const RCF = apiJson?.data?.RiskyChoiceFraming;
                if (!RCF) {
                    setGlobalRCF([]);
                    setLoadingGlobal(false);
                    return;
                }
                const arr = [
                    {
                        scenario: scenario1,
                        [option1Label]: RCF["1"]?.[1] ?? 0,
                        [option2Label]: RCF["1"]?.[2] ?? 0
                    },
                    {
                        scenario: scenario2,
                        [option1Label]: RCF["2"]?.[1] ?? 0,
                        [option2Label]: RCF["2"]?.[2] ?? 0
                    }
                ];
                setGlobalRCF(arr);
                setLoadingGlobal(false);
            }).catch(() => {
            setGlobalRCF([]);
            setLoadingGlobal(false);
        });
    }, [scenario1, scenario2, option1Label, option2Label]);

    if (loading) {
        return <div style={{
            textAlign: "center",
            marginTop: 50,
            fontSize: 17,
            color: colors.brillantBlue
        }}>{loadingText}</div>;
    }

    // Bar chart settings
    const thinBarSize = 28;

    return (
        <div className="experiment-container" style={commonStyles.container}>
            <h2 style={{
                ...commonStyles.heading,
                margin: "0 0 24px 0",
                fontSize: 28,
                fontWeight: 700
            }}>
                {t('experiments', 'RiskyChoiceFraming', 'screens', 'resultsGlobal', 'title')}
            </h2>
            <div style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 48,
                marginTop: 24,
                width: "100%"
            }}>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {yourClassTitle}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart
                                data={dataOwnGroup}
                                margin={{ top: 30, right: 20, left: 10, bottom: 40 }}
                                layout="vertical"
                                barSize={thinBarSize}
                                barCategoryGap="40%"
                                barGap={12}
                            >
                                <XAxis type="number" allowDecimals={false} label={{ value: yAxisLabel, position: "insideTopLeft", offset: 0 }}/>
                                <YAxis dataKey="scenario" type="category" width={210} />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend />
                                <Bar dataKey={option1Label} fill={safeColor} name={option1Label}>
                                    <LabelList dataKey={option1Label} position="right" fontSize={15} />
                                </Bar>
                                <Bar dataKey={option2Label} fill={riskyColor} name={option2Label}>
                                    <LabelList dataKey={option2Label} position="right" fontSize={15} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div style={cardStyle}>
                    <h3 style={{
                        ...commonStyles.heading,
                        fontSize: 20,
                        marginBottom: 10
                    }}>
                        {allClassesTitle ?? "Alle Klassen (gesamt)"}
                    </h3>
                    <div style={{ flex: 1, width: "100%" }}>
                        {loadingGlobal ? (
                            <div style={{ color: colors.brillantBlue, fontSize: 17, textAlign: "center", marginTop: 40 }}>
                                {loadingText}
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height={260}>
                                <BarChart
                                    data={globalRCF}
                                    margin={{ top: 30, right: 20, left: 10, bottom: 40 }}
                                    layout="vertical"
                                    barSize={thinBarSize}
                                    barCategoryGap="40%"
                                    barGap={12}
                                >
                                    <XAxis type="number" allowDecimals={false} label={{ value: yAxisLabel, position: "insideTopLeft", offset: 0 }}/>
                                    <YAxis dataKey="scenario" type="category" width={210} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Bar dataKey={option1Label} fill={safeColor} name={option1Label}>
                                        <LabelList dataKey={option1Label} position="right" fontSize={15} />
                                    </Bar>
                                    <Bar dataKey={option2Label} fill={riskyColor} name={option2Label}>
                                        <LabelList dataKey={option2Label} position="right" fontSize={15} />
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ResultScreenGlobal: ScreenConfig = {
    component: ResultScreenGlobalComponent,
    defaultResult: {
        selections: [],
    },
    canContinueDefault: true,
};